<script lang="ts" setup>
import { computed } from 'vue'
import { useRuntimeConfig } from '#app'
import UserInfo from '~mobile/pages/account/user-info.vue'
import AgencyWallet from '~mobile/pages/account/agency-wallet.vue'
import { AccountTabs } from '~/constants/account'
import { depositMethodsMobile, getWithdrawMethodsMobile } from '~/resources/account/account-dashboard'
import { useTransfer } from '~/composables/transfer/use-transfer'
import { useLogout } from '~/composables/account/use-logout'
import { useDepositData } from '~/composables/deposit/use-deposit-data'
import { useTpay } from '~/composables/deposit/use-tpay'
import { DEPOSIT_METHOD } from '~/constants'

const { creditRate, betAccounts } = useTransfer()
const { INDIRECT_BRAND_NAME, INDIRECT_BRAND2_NAME, IS_P2P_TRADING_ENABLED } = useRuntimeConfig().public

const { submitLogout } = useLogout()
const { isMaintain, fetchAllDepositData } = useDepositData()
const withdrawMethodsMobile = getWithdrawMethodsMobile()
const { isTpayMaintenance, checkTpayStatus } = useTpay()

fetchAllDepositData()
const filterPaymentMethods = computed(() => {
  return isTpayMaintenance.value
    ? depositMethodsMobile.filter(method => {
        return method.method !== DEPOSIT_METHOD.T_PAY
      })
    : depositMethodsMobile
})
const depositMethods = computed(() => {
  return filterPaymentMethods.value.map(method => {
    return {
      ...method,
      isMaintain: isMaintain(method.method)
    }
  })
})
onMounted(() => {
  checkTpayStatus()
})
</script>
<template>
  <div class="ctn-account">
    <div class="px-3">
      <UserInfo />
    </div>

    <div class="ctn-wallet d-flex">
      <AgencyWallet
        :agencyName="INDIRECT_BRAND_NAME"
        :accounts="betAccounts[INDIRECT_BRAND_NAME]"
        :creditRate="creditRate"
        iconClass="icon-global"
        hasNote
      />
      <AgencyWallet
        :agencyName="INDIRECT_BRAND2_NAME"
        :accounts="betAccounts[INDIRECT_BRAND2_NAME]"
        :creditRate="creditRate"
        iconClass="icon-exchange"
        hasNote
        variant="yellow"
      />
    </div>

    <div class="ctn-deposit ctn-item">
      <a
        class="ctn-item__collapse"
        data-bs-toggle="collapse"
        data-bs-target="#depositCollapsed"
        aria-expanded="true"
        aria-controls="depositCollapsed"
        role="button"
        data-toggle="collapse"
        href="#depositCollapsed"
      >
        <div class="ctn-item__info">
          <div class="ctn-item__icon">
            <img src="/assets/images/icons/deposit.svg" alt="Deposit" />
          </div>
          <span class="ctn-item__title">Nạp tiền</span>
        </div>
        <span class="icon-arrow-down"></span>
      </a>
      <div id="depositCollapsed" class="accordion-collapse collapse show">
        <NuxtLink
          v-for="(method, index) in depositMethods"
          :key="index"
          :class="['method-item', { disabled: method.isMaintain }]"
          :to="method.link"
        >
          <span v-if="method.isMaintain" class="method-item__label">Bảo trì</span>
          <img class="method-item__image" :src="method.icon" :alt="method.name" />
          <span class="method-item__name">{{ method.name }}</span>
        </NuxtLink>
      </div>
    </div>
    <div class="ctn-withdraw ctn-item">
      <a
        class="ctn-item__collapse"
        data-bs-toggle="collapse"
        data-bs-target="#withdrawCollapsed"
        aria-expanded="true"
        aria-controls="withdrawCollapsed"
        role="button"
        data-toggle="collapse"
        href="#withdrawCollapsed"
      >
        <div class="ctn-item__info">
          <div class="ctn-item__icon">
            <img src="/assets/images/icons/withdraw.svg" alt="Withdraw" />
          </div>
          <span class="ctn-item__title">Rút tiền</span>
        </div>
        <span class="icon-arrow-down"></span>
      </a>
      <div id="withdrawCollapsed" class="accordion-collapse collapse show">
        <NuxtLink
          v-for="(method, index) in withdrawMethodsMobile"
          :key="index"
          :class="['method-item', { disabled: method.isMaintain }]"
          :to="method.link"
        >
          <span v-if="method.isMaintain" class="method-item__label">Bảo trì</span>
          <img v-if="method.label" class="method-item__label" :src="method.label" alt="label" />
          <img class="method-item__image" :src="method.icon" :alt="method.name" />
          <span class="method-item__name">{{ method.name }}</span>
        </NuxtLink>
      </div>
    </div>
    <div class="ctn-item">
      <NuxtLink class="ctn-item__info" :to="AccountTabs.TRANSFER_MONEY">
        <div class="ctn-item__icon">
          <img src="/assets/images/icons/transfer.svg" alt="Transfer" />
        </div>
        <span class="ctn-item__title">Chuyển ví</span>
      </NuxtLink>
    </div>
    <div v-if="IS_P2P_TRADING_ENABLED" class="ctn-item">
      <NuxtLink class="ctn-item__info" :to="AccountTabs.COIN12">
        <div class="ctn-item__icon coin12-icon">12</div>
        <span class="ctn-item__title">Giao dịch Coin12</span>
      </NuxtLink>
    </div>
    <div class="ctn-item">
      <NuxtLink class="ctn-item__info" :to="AccountTabs.HISTORY_TRANSACTION">
        <div class="ctn-item__icon">
          <img src="/assets/images/icons/txn-history.svg" alt="Transactions" />
        </div>
        <span class="ctn-item__title">Lịch sử giao dịch</span>
      </NuxtLink>
    </div>
    <div class="ctn-item">
      <NuxtLink class="ctn-item__info" :to="AccountTabs.HISTORY_BETTING">
        <div class="ctn-item__icon">
          <img src="/assets/images/icons/bet-history.svg" alt="Bet Histories" />
        </div>
        <span class="ctn-item__title">Lịch sử cá cược</span>
      </NuxtLink>
    </div>
    <div class="ctn-item">
      <button class="ctn-item__info" @click="submitLogout()">
        <div class="ctn-item__icon">
          <img src="/assets/images/icons/exit.svg" alt="Logout" />
        </div>
        <span class="ctn-item__title">Đăng xuất</span>
      </button>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import '~scssAsset/mobile/pages/account/account-section.scss';
</style>
